import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import BankFields from "@/components/BankFields";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, Upload, FileText, Trash2 } from "lucide-react";

const DOC_TYPES = [
  { k: "pan", l: "PAN Card" },
  { k: "aadhar", l: "Aadhar" },
  { k: "cheque", l: "Cancelled Cheque" },
  { k: "selfie", l: "Selfie" },
];

export default function DistKyc() {
  const { user, refresh } = useAuth();
  const [k, setK] = useState({
    pan: "", aadhar: "",
    account_holder: "", bank_name: "", bank_name_other: "",
    bank_account: "", ifsc: "", branch: "",
    nominee_name: "", nominee_relation: "",
    address: "", city: "", state: "",
  });
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(null);
  const refs = useRef({});

  const loadFiles = () => api.get("/kyc/files").then((r) => setFiles(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/kyc/me").then((r) => {
      if (r.data && r.data.id) { setExisting(r.data); setK((prev) => ({ ...prev, ...r.data })); }
    });
    // Auto-prefill address + bank + PAN fields from extended profile (registration data)
    api.get("/dist/profile-extended").then((r) => {
      setK((prev) => ({
        ...prev,
        pan:          prev.pan          || r.data.pan_number   || "",
        address:      prev.address      || r.data.full_address || "",
        city:         prev.city         || r.data.city         || "",
        state:        prev.state        || r.data.state        || "",
        nominee_name: prev.nominee_name || r.data.father_name  || "",
        bank_name:    prev.bank_name    || r.data.bank_name    || "",
        bank_account: prev.bank_account || r.data.bank_account || "",
        ifsc:         prev.ifsc         || r.data.ifsc         || "",
      }));
    }).catch(() => {});
    loadFiles();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...k };
      if (payload.bank_name?.startsWith("Other") && payload.bank_name_other) {
        payload.bank_name = payload.bank_name_other.trim();
      }
      delete payload.bank_name_other;
      await api.post("/kyc", payload);
      toast.success("KYC details saved for review");
      await refresh();
      const { data } = await api.get("/kyc/me");
      setExisting(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const upload = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    try {
      await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${docType.toUpperCase()} uploaded`);
      await loadFiles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); }
  };

  const StatusBadge = () => {
    const map = {
      approved: { i: CheckCircle2, c: "olive", t: "Approved" },
      pending: { i: Clock, c: "gold-deep", t: "Pending review" },
      rejected: { i: XCircle, c: "rose", t: "Rejected" },
      not_submitted: { i: Clock, c: "navy/40", t: "Not submitted" },
    };
    const s = map[user?.kyc_status || "not_submitted"] || map.not_submitted;
    const Icon = s.i;
    return (
      <div className="inline-flex items-center gap-2 text-sm" data-testid="kyc-status">
        <Icon size={16} className={`text-${s.c}`} /> {s.t}
      </div>
    );
  };

  const fileFor = (docType) => files.find((f) => f.doc_type === docType);

  return (
    <DashboardLayout title="KYC Verification" subtitle="Submit identity, bank details and documents to unlock payouts" actions={<StatusBadge />}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-border p-6 lg:p-8 rounded-sm">
          <h3 className="font-serif text-xl text-navy mb-4">Identity details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field testid="kyc-pan" label="PAN" required value={k.pan} onChange={(v) => setK({ ...k, pan: v.toUpperCase() })} placeholder="ABCDE1234F" />
            <Field testid="kyc-aadhar" label="Aadhar" required value={k.aadhar} onChange={(v) => setK({ ...k, aadhar: v })} placeholder="1234 5678 9012" />
            <Field testid="kyc-nominee" label="Nominee Name" value={k.nominee_name} onChange={(v) => setK({ ...k, nominee_name: v })} />
            <Field testid="kyc-nominee-rel" label="Nominee Relation" value={k.nominee_relation} onChange={(v) => setK({ ...k, nominee_relation: v })} />
          </div>

          <div className="mt-6 pt-5 border-t border-line/70">
            <h3 className="font-serif text-xl text-navy mb-4">Bank Account (payouts)</h3>
            <BankFields values={k} onChange={setK} required testidPrefix="kyc" />
          </div>
          <button data-testid="kyc-submit" disabled={busy} className="mt-6 bg-navy text-white px-6 py-3 hover:bg-navy-deep rounded-sm disabled:opacity-50">
            {busy ? "Saving..." : existing ? "Update & Resubmit" : "Submit for review"}
          </button>
        </form>

        <aside className="bg-white border border-border p-6 rounded-sm h-fit">
          <h3 className="font-serif text-xl text-navy mb-2">Documents</h3>
          <p className="text-xs text-navy/50 mb-5">Upload clear photos or PDFs. Max 5MB each.</p>
          <div className="space-y-3">
            {DOC_TYPES.map((d) => {
              const f = fileFor(d.k);
              return (
                <div key={d.k} className="flex items-center gap-3 border border-border p-3 rounded-sm">
                  <FileText className="text-gold shrink-0" size={18} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-navy font-medium">{d.l}</div>
                    {f ? (
                      <div className="text-[10px] text-olive truncate">✓ {f.original_filename}</div>
                    ) : (
                      <div className="text-[10px] text-navy/40">Not uploaded</div>
                    )}
                  </div>
                  <input ref={(el) => (refs.current[d.k] = el)} type="file" hidden accept="image/*,application/pdf" onChange={(e) => upload(d.k, e.target.files?.[0])} data-testid={`kyc-file-${d.k}`} />
                  <button onClick={() => refs.current[d.k]?.click()} disabled={uploading === d.k} data-testid={`kyc-upload-btn-${d.k}`} className="p-2 bg-navy text-white rounded-sm hover:bg-navy-deep disabled:opacity-50">
                    {uploading === d.k ? <Clock size={14} /> : <Upload size={14} />}
                  </button>
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, value, onChange, required, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-navy/60">{label}</label>
      <input data-testid={testid} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full border border-border px-3 py-3 text-sm rounded-sm focus:border-gold focus:outline-none" />
    </div>
  );
}
